import { queryOptions, useQuery } from '@tanstack/react-query';

import {
  POLLING_DELAY_MS,
  SECONDS_IN_HOUR,
  SECONDS_IN_DAY,
} from '@cast/constants';
import { DateRange, QueryRefetchInterval } from '@cast/shared-types';
import { ClusterEfficiencyResponse } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const CLUSTER_EFFICIENCY_DETAILS_QUERY_KEY = 'cluster-efficiency-details';

const getClusterEfficiencyDetailsQueryKey = ({
  clusterId,
  fromStr,
  toStr,
  useListingPrices,
  stepSeconds,
}: {
  clusterId: string;
  fromStr: string;
  toStr: string;
  useListingPrices: boolean;
  stepSeconds: typeof SECONDS_IN_HOUR | typeof SECONDS_IN_DAY | undefined;
}) =>
  [
    CLUSTER_EFFICIENCY_DETAILS_QUERY_KEY,
    clusterId,
    fromStr,
    toStr,
    useListingPrices,
    stepSeconds,
  ] as const;

type UseClusterEfficiencyDetailsArgs = {
  clusterId: string;
  range: DateRange;
  useListingPrices: boolean;
  stepSeconds?: typeof SECONDS_IN_HOUR | typeof SECONDS_IN_DAY;
  refetchInterval?: QueryRefetchInterval<ClusterEfficiencyResponse>;
  enabled?: boolean;
};

const getClusterEfficiencyDetailsQueryOptions = ({
  clusterId,
  range: [from, to],
  useListingPrices,
  stepSeconds,
  refetchInterval = POLLING_DELAY_MS,
  enabled = true,
}: UseClusterEfficiencyDetailsArgs) => {
  const fromStr = from.toISOString();
  const toStr = to.toISOString();

  return queryOptions({
    queryKey: getClusterEfficiencyDetailsQueryKey({
      clusterId,
      fromStr,
      toStr,
      useListingPrices,
      stepSeconds,
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getClusterEfficiencyDetails({
        clusterId,
        startTime: fromStr,
        endTime: toStr,
        stepSeconds,
        useListingPrices,
      });

      return data;
    },
    refetchInterval,
    enabled,
  });
};

export const useClusterEfficiencyDetailsQuery = (
  args: UseClusterEfficiencyDetailsArgs
) => {
  const { data, isLoading, error, refetch } = useQuery(
    getClusterEfficiencyDetailsQueryOptions(args)
  );

  return { efficiency: data, isLoading, error, refetch };
};
